import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, User, Phone, Mail, FileText, 
  Check, X, Loader2, Filter, Search, ChevronLeft,
  AlertCircle, CheckCircle2, XCircle, Clock4, LogOut
} from 'lucide-react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar as CalendarPicker } from './ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { 
    label: 'Pending', 
    labelAr: 'قيد الانتظار',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock4
  },
  confirmed: { 
    label: 'Confirmed', 
    labelAr: 'مؤكد',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: CheckCircle2
  },
  completed: { 
    label: 'Completed', 
    labelAr: 'مكتمل',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: Check
  },
  cancelled: { 
    label: 'Cancelled', 
    labelAr: 'ملغى',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: XCircle
  },
};

export const AdminDashboard = ({ language = 'en', onBack, onLogout }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [updating, setUpdating] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
  });

  const isRTL = language === 'ar';

  const t = {
    en: {
      title: 'Admin Dashboard',
      appointments: 'Appointments',
      totalAppointments: 'Total Appointments',
      pending: 'Pending',
      confirmed: 'Confirmed',
      completed: 'Completed',
      cancelled: 'Cancelled',
      filterByStatus: 'Filter by Status',
      filterByDate: 'Filter by Date',
      searchPlaceholder: 'Search by name or phone...',
      allStatuses: 'All Statuses',
      noAppointments: 'No appointments found',
      appointmentDetails: 'Appointment Details',
      customer: 'Customer',
      phone: 'Phone',
      email: 'Email',
      service: 'Service',
      date: 'Date',
      time: 'Time',
      notes: 'Notes',
      status: 'Status',
      updateStatus: 'Update Status',
      back: 'Back to Website',
      logout: 'Logout',
      createdAt: 'Created At',
      clearFilters: 'Clear Filters',
      today: 'Today',
    },
    ar: {
      title: 'لوحة التحكم',
      appointments: 'المواعيد',
      totalAppointments: 'إجمالي المواعيد',
      pending: 'قيد الانتظار',
      confirmed: 'مؤكد',
      completed: 'مكتمل',
      cancelled: 'ملغى',
      filterByStatus: 'فلتر حسب الحالة',
      filterByDate: 'فلتر حسب التاريخ',
      searchPlaceholder: 'البحث بالاسم أو الهاتف...',
      allStatuses: 'جميع الحالات',
      noAppointments: 'لا توجد مواعيد',
      appointmentDetails: 'تفاصيل الموعد',
      customer: 'العميلة',
      phone: 'الهاتف',
      email: 'البريد الإلكتروني',
      service: 'الخدمة',
      date: 'التاريخ',
      time: 'الوقت',
      notes: 'ملاحظات',
      status: 'الحالة',
      updateStatus: 'تحديث الحالة',
      back: 'العودة للموقع',
      logout: 'تسجيل خروج',
      createdAt: 'تاريخ الإنشاء',
      clearFilters: 'مسح الفلاتر',
      today: 'اليوم',
    },
  };

  const text = t[language];

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/appointments`);
      const data = response.data;
      setAppointments(data);
      
      // Calculate stats
      const newStats = {
        total: data.length,
        pending: data.filter(a => a.status === 'pending').length,
        confirmed: data.filter(a => a.status === 'confirmed').length,
        completed: data.filter(a => a.status === 'completed').length,
        cancelled: data.filter(a => a.status === 'cancelled').length,
      };
      setStats(newStats);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      setUpdating(true);
      await axios.patch(`${API}/appointments/${appointmentId}`, {
        status: newStatus,
      });
      await fetchAppointments();
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Error updating appointment:', error);
    } finally {
      setUpdating(false);
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    // Status filter
    if (filterStatus !== 'all' && apt.status !== filterStatus) return false;
    
    // Date filter
    if (filterDate && apt.date !== format(filterDate, 'yyyy-MM-dd')) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = apt.customer_name?.toLowerCase().includes(query);
      const matchesPhone = apt.customer_phone?.toLowerCase().includes(query);
      if (!matchesName && !matchesPhone) return false;
    }
    
    return true;
  });

  // Sort by date (newest first)
  const sortedAppointments = [...filteredAppointments].sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateB - dateA;
  });

  const StatCard = ({ title, value, color, icon: Icon }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E5] ${color}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#7D7D7D] mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#4A4A4A]">{value}</p>
        </div>
        <div className="w-12 h-12 rounded-full bg-[#F9F7F2] flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#D4A39A]" />
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className={`min-h-screen bg-[#F9F7F2] ${isRTL ? 'rtl' : 'ltr'}`} data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-10">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="admin-back-btn"
                variant="ghost"
                onClick={onBack}
                className="flex items-center gap-2 text-[#7D7D7D] hover:text-[#4A4A4A]"
              >
                <ChevronLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                {text.back}
              </Button>
              <div className="h-6 w-px bg-[#E5E5E5]" />
              <h1 className="text-xl md:text-2xl font-bold text-[#4A4A4A] heading">
                {text.title}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-2xl font-bold text-[#D4A39A] heading hidden md:block">
                {language === 'ar' ? 'سيرينا' : 'Sirena'}
              </div>
              {onLogout && (
                <Button
                  data-testid="admin-logout-btn"
                  variant="outline"
                  onClick={onLogout}
                  className="flex items-center gap-2 text-red-500 border-red-200 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">{text.logout}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard 
            title={text.totalAppointments} 
            value={stats.total} 
            icon={Calendar}
          />
          <StatCard 
            title={text.pending} 
            value={stats.pending} 
            icon={Clock4}
          />
          <StatCard 
            title={text.confirmed} 
            value={stats.confirmed} 
            icon={CheckCircle2}
          />
          <StatCard 
            title={text.completed} 
            value={stats.completed} 
            icon={Check}
          />
          <StatCard 
            title={text.cancelled} 
            value={stats.cancelled} 
            icon={XCircle}
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-[#E5E5E5] mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-[#7D7D7D] ${isRTL ? 'right-3' : 'left-3'}`} />
              <Input
                data-testid="admin-search"
                placeholder={text.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'}`}
              />
            </div>

            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="admin-status-filter" className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder={text.filterByStatus} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{text.allStatuses}</SelectItem>
                <SelectItem value="pending">{text.pending}</SelectItem>
                <SelectItem value="confirmed">{text.confirmed}</SelectItem>
                <SelectItem value="completed">{text.completed}</SelectItem>
                <SelectItem value="cancelled">{text.cancelled}</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="admin-date-filter"
                  variant="outline"
                  className="w-full md:w-48 justify-start"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {filterDate ? format(filterDate, 'PP', { locale: isRTL ? ar : enUS }) : text.filterByDate}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarPicker
                  mode="single"
                  selected={filterDate}
                  onSelect={setFilterDate}
                  locale={isRTL ? ar : enUS}
                />
              </PopoverContent>
            </Popover>

            {/* Clear Filters */}
            {(filterStatus !== 'all' || filterDate || searchQuery) && (
              <Button
                data-testid="admin-clear-filters"
                variant="ghost"
                onClick={() => {
                  setFilterStatus('all');
                  setFilterDate(null);
                  setSearchQuery('');
                }}
                className="text-[#D4A39A]"
              >
                <X className="w-4 h-4 mr-1" />
                {text.clearFilters}
              </Button>
            )}
          </div>
        </div>

        {/* Appointments List */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E5E5] overflow-hidden">
          <div className="p-4 md:p-6 border-b border-[#E5E5E5]">
            <h2 className="text-lg font-semibold text-[#4A4A4A]">
              {text.appointments} ({sortedAppointments.length})
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#D4A39A]" />
            </div>
          ) : sortedAppointments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-[#7D7D7D]">
              <AlertCircle className="w-12 h-12 mb-4 text-[#D4A39A]" />
              <p>{text.noAppointments}</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E5E5E5]">
              {sortedAppointments.map((apt, index) => {
                const StatusIcon = statusConfig[apt.status]?.icon || Clock4;
                return (
                  <motion.div
                    key={apt.id}
                    data-testid={`appointment-row-${index}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setSelectedAppointment(apt)}
                    className="p-4 md:p-6 hover:bg-[#F9F7F2] cursor-pointer transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#D4A39A] flex items-center justify-center text-white font-semibold flex-shrink-0">
                          {apt.customer_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#4A4A4A]">
                            {apt.customer_name}
                          </h3>
                          <p className="text-sm text-[#7D7D7D] flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {apt.customer_phone}
                          </p>
                          <p className="text-sm text-[#D4A39A] mt-1">
                            {apt.service}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
                        <div className="text-sm">
                          <p className="text-[#4A4A4A] flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-[#7D7D7D]" />
                            {format(parseISO(apt.date), 'PP', { locale: isRTL ? ar : enUS })}
                          </p>
                          <p className="text-[#7D7D7D] flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {apt.time}
                          </p>
                        </div>

                        <Badge 
                          className={`${statusConfig[apt.status]?.color} border`}
                          data-testid={`appointment-status-${index}`}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {isRTL ? statusConfig[apt.status]?.labelAr : statusConfig[apt.status]?.label}
                        </Badge>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Appointment Details Modal */}
      <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
        <DialogContent className="max-w-md" data-testid="appointment-details-modal">
          <DialogHeader>
            <DialogTitle className="heading text-xl">
              {text.appointmentDetails}
            </DialogTitle>
          </DialogHeader>

          {selectedAppointment && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-[#F9F7F2] rounded-lg">
                <div className="w-14 h-14 rounded-full bg-[#D4A39A] flex items-center justify-center text-white text-xl font-semibold">
                  {selectedAppointment.customer_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold text-[#4A4A4A] text-lg">
                    {selectedAppointment.customer_name}
                  </h3>
                  <Badge className={`${statusConfig[selectedAppointment.status]?.color} border mt-1`}>
                    {isRTL ? statusConfig[selectedAppointment.status]?.labelAr : statusConfig[selectedAppointment.status]?.label}
                  </Badge>
                </div>
              </div>

              <div className="grid gap-3">
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-[#D4A39A]" />
                  <span className="text-[#7D7D7D]">{text.phone}:</span>
                  <span className="text-[#4A4A4A]">{selectedAppointment.customer_phone}</span>
                </div>

                {selectedAppointment.customer_email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-[#D4A39A]" />
                    <span className="text-[#7D7D7D]">{text.email}:</span>
                    <span className="text-[#4A4A4A]">{selectedAppointment.customer_email}</span>
                  </div>
                )}

                <div className="flex items-center gap-3 text-sm">
                  <FileText className="w-4 h-4 text-[#D4A39A]" />
                  <span className="text-[#7D7D7D]">{text.service}:</span>
                  <span className="text-[#4A4A4A]">{selectedAppointment.service}</span>
                </div>

                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-[#D4A39A]" />
                  <span className="text-[#7D7D7D]">{text.date}:</span>
                  <span className="text-[#4A4A4A]">
                    {format(parseISO(selectedAppointment.date), 'PPP', { locale: isRTL ? ar : enUS })}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-[#D4A39A]" />
                  <span className="text-[#7D7D7D]">{text.time}:</span>
                  <span className="text-[#4A4A4A]">{selectedAppointment.time}</span>
                </div>

                {selectedAppointment.notes && (
                  <div className="flex items-start gap-3 text-sm">
                    <FileText className="w-4 h-4 text-[#D4A39A] mt-0.5" />
                    <span className="text-[#7D7D7D]">{text.notes}:</span>
                    <span className="text-[#4A4A4A]">{selectedAppointment.notes}</span>
                  </div>
                )}
              </div>

              {/* Status Update */}
              <div className="pt-4 border-t border-[#E5E5E5]">
                <p className="text-sm text-[#7D7D7D] mb-3">{text.updateStatus}</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(statusConfig).map(([status, config]) => (
                    <Button
                      key={status}
                      data-testid={`status-btn-${status}`}
                      variant={selectedAppointment.status === status ? 'default' : 'outline'}
                      size="sm"
                      disabled={updating || selectedAppointment.status === status}
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, status)}
                      className={`${selectedAppointment.status === status ? 'bg-[#D4A39A] hover:bg-[#C59289]' : ''}`}
                    >
                      {updating && selectedAppointment.status !== status ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <config.icon className="w-4 h-4 mr-1" />
                      )}
                      {isRTL ? config.labelAr : config.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
